import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { IconChevronComponent } from '../icon-chevron/icon-chevron.component';
import { ButtonComponent } from '../button/button.component';
import { DropdownCoordinatorService } from '../../../core/services/dropdown-coordinator/dropdown-coordinator.service';

export interface DatePresetOption {
  id: string;
  labelKey: string;
}

@Component({
  selector: 'app-date-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    IconChevronComponent,
    ButtonComponent
  ],
  templateUrl: './date-filter.component.html',
  styleUrl: './date-filter.component.css'
})
export class DateFilterComponent implements OnInit {
  private static nextId = 0;
  private readonly autoId = `app_date_filter_${DateFilterComponent.nextId++}`;

  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private dropdownCoordinator = inject(DropdownCoordinatorService);

  @Input() id: string = '';
  @Input() label: string = 'Date';
  @Input() pillClass: string = 'w-[165px]';
  @Input() dropdownClass: string = 'w-72';
  @Input() align: 'left' | 'right' = 'right';
  @Input() disabled: boolean = false;

  @Input() formGroup?: FormGroup;
  @Input() startControlName: string = 'dateStart';
  @Input() endControlName: string = 'dateEnd';

  @Input() startDate: string = '';
  @Input() endDate: string = '';

  @Output() startDateChange = new EventEmitter<string>();
  @Output() endDateChange = new EventEmitter<string>();
  @Output() dateChange = new EventEmitter<{ start: string; end: string }>();
  @Output() apply = new EventEmitter<void>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  internalForm = new FormGroup({
    dateStart: new FormControl(''),
    dateEnd: new FormControl('')
  });

  get activeFormGroup(): FormGroup {
    return this.formGroup || this.internalForm;
  }

  get activeStartControlName(): string {
    return this.formGroup ? this.startControlName : 'dateStart';
  }

  get activeEndControlName(): string {
    return this.formGroup ? this.endControlName : 'dateEnd';
  }

  get filterId(): string {
    return this.id || this.autoId;
  }

  isOpen = computed(() => this.dropdownCoordinator.isOpen(this.filterId));
  selectedPreset = signal<string>('');

  readonly presets: DatePresetOption[] = [
    { id: 'today', labelKey: 'REQUESTLIST.FILTERS.CALENDAR_DATE.TODAY' },
    { id: '7days', labelKey: 'REQUESTLIST.FILTERS.CALENDAR_DATE.LAST_7_DAYS' },
    { id: '30days', labelKey: 'REQUESTLIST.FILTERS.CALENDAR_DATE.LAST_30_DAYS' },
    { id: 'thisMonth', labelKey: 'REQUESTLIST.FILTERS.CALENDAR_DATE.THIS_MONTH' },
    { id: 'lastMonth', labelKey: 'REQUESTLIST.FILTERS.CALENDAR_DATE.LAST_MONTH' }
  ];

  @HostBinding('class') hostClass = 'inline-block';

  get currentStartDate(): string {
    return this.activeFormGroup.get(this.activeStartControlName)?.value || '';
  }

  get currentEndDate(): string {
    return this.activeFormGroup.get(this.activeEndControlName)?.value || '';
  }

  get dateSummary(): string | null {
    const preset = this.selectedPreset();
    if (
      preset === 'today' ||
      preset === '7days' ||
      preset === '30days' ||
      preset === 'thisMonth' ||
      preset === 'lastMonth'
    ) {
      return 'x1';
    }
    if (this.currentStartDate || this.currentEndDate) {
      return 'x1';
    }
    return null;
  }

  ngOnInit(): void {
    if (!this.formGroup && (this.startDate || this.endDate)) {
      this.internalForm.patchValue({
        dateStart: this.startDate,
        dateEnd: this.endDate
      });
    }

    if (this.currentStartDate || this.currentEndDate) {
      this.selectedPreset.set(this.detectPreset(this.currentStartDate, this.currentEndDate));
    }

    this.activeFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const start = this.currentStartDate;
        const end = this.currentEndDate;
        if (!start && !end) {
          this.selectedPreset.set('');
        }
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.close();
    }
  }

  toggle(): void {
    if (this.disabled) return;
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled || this.isOpen()) return;
    this.dropdownCoordinator.open(this.filterId);
    this.opened.emit();
  }

  close(): void {
    if (!this.isOpen()) return;
    this.dropdownCoordinator.close(this.filterId);
    this.closed.emit();
  }

  selectPreset(preset: string): void {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const formatDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    let start = '';
    let end = formatDate(now);

    if (preset === 'today') {
      start = formatDate(now);
    } else if (preset === '7days') {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      start = formatDate(d);
    } else if (preset === '30days') {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      start = formatDate(d);
    } else if (preset === 'thisMonth') {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      start = formatDate(d);
    } else if (preset === 'lastMonth') {
      const startLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endLast = new Date(now.getFullYear(), now.getMonth(), 0);
      start = formatDate(startLast);
      end = formatDate(endLast);
    }

    this.setDateRange(start, end, preset);
  }

  onCustomDateChange(): void {
    this.selectedPreset.set('custom');
    if (!this.formGroup) {
      this.startDate = this.currentStartDate;
      this.endDate = this.currentEndDate;
      this.startDateChange.emit(this.startDate);
      this.endDateChange.emit(this.endDate);
    }
    this.dateChange.emit({
      start: this.currentStartDate,
      end: this.currentEndDate
    });
  }

  setDateRange(start: string, end: string, preset: string = ''): void {
    this.selectedPreset.set(preset);
    const patch: Record<string, any> = {};
    patch[this.activeStartControlName] = start;
    patch[this.activeEndControlName] = end;
    this.activeFormGroup.patchValue(patch);
    this.activeFormGroup.get(this.activeStartControlName)?.markAsDirty();
    this.activeFormGroup.get(this.activeEndControlName)?.markAsDirty();

    if (!this.formGroup) {
      this.startDate = start;
      this.endDate = end;
      this.startDateChange.emit(start);
      this.endDateChange.emit(end);
    }
    this.dateChange.emit({ start, end });
  }

  onApply(): void {
    this.close();
    this.apply.emit();
  }

  reset(): void {
    this.selectedPreset.set('');
    this.setDateRange('', '', '');
  }

  detectPreset(start: string, end: string): string {
    if (!start && !end) return '';
    if (!start || !end) return 'custom';
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const formatDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const todayStr = formatDate(now);

    if (start === todayStr && end === todayStr) return 'today';

    const d7 = new Date();
    d7.setDate(d7.getDate() - 6);
    if (start === formatDate(d7) && end === todayStr) return '7days';

    const d30 = new Date();
    d30.setDate(d30.getDate() - 29);
    if (start === formatDate(d30) && end === todayStr) return '30days';

    const thisMonthStart = formatDate(new Date(now.getFullYear(), now.getMonth(), 1));
    if (start === thisMonthStart && end === todayStr) return 'thisMonth';

    const lastMonthStart = formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    const lastMonthEnd = formatDate(new Date(now.getFullYear(), now.getMonth(), 0));
    if (start === lastMonthStart && end === lastMonthEnd) return 'lastMonth';

    return 'custom';
  }
}
